/**
 * TRUS-719 (Integrated Inbox, phase 4): play a voicemail in the staff thread.
 *
 * The audio comes from GET /api/messaging/channels/{c}/messages/{m}/voicemail-audio,
 * fetched with the staff session (an <audio src> cannot carry the bearer
 * token), and is played from an object URL that is revoked on unmount. It is
 * fetched on the first Play, not when the thread renders.
 *
 *  - 410 recording_unavailable → "Recording no longer available".
 *  - 404 / 501 → no playback on this backend: the player hides for the session.
 *  - Anything else → an error line with Retry.
 */
import { useEffect, useRef, useState } from 'react';
import { Loader2, Play, RotateCcw } from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { cn } from '@/lib/utils';
import { fetchVoicemailAudio } from '@/services/inboxPhase4Api';

type Tone = 'default' | 'inverted';

/** Set once a backend answers 404 / 501: no voicemail playback this session. */
let playbackUnavailable = false;

/** Tests only. */
export function resetVoicemailPlaybackSession(): void {
  playbackUnavailable = false;
}

type State =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready'; url: string }
  | { kind: 'gone' }
  | { kind: 'error' }
  | { kind: 'hidden' };

interface VoicemailPlayerProps {
  channelId: number;
  messageId: number;
  tone?: Tone;
}

export function VoicemailPlayer({ channelId, messageId, tone = 'default' }: VoicemailPlayerProps) {
  const { t } = useAppTranslation();
  const [state, setState] = useState<State>(() => (playbackUnavailable ? { kind: 'hidden' } : { kind: 'idle' }));
  const urlRef = useRef<string | null>(null);
  const mounted = useRef(true);
  const inverted = tone === 'inverted';

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
    };
  }, []);

  // A different message in the same slot: start over.
  useEffect(() => {
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    }
    setState(playbackUnavailable ? { kind: 'hidden' } : { kind: 'idle' });
  }, [channelId, messageId]);

  const load = async () => {
    setState({ kind: 'loading' });
    try {
      const res = await fetchVoicemailAudio(channelId, messageId);
      if (!mounted.current) return;
      if (res.kind === 'gone') {
        setState({ kind: 'gone' });
      } else if (res.kind === 'missing') {
        playbackUnavailable = true;
        setState({ kind: 'hidden' });
      } else {
        const url = URL.createObjectURL(res.blob);
        urlRef.current = url;
        setState({ kind: 'ready', url });
      }
    } catch {
      if (mounted.current) setState({ kind: 'error' });
    }
  };

  if (state.kind === 'hidden') return null;

  const mutedText = inverted ? 'text-white/80' : 'text-muted-foreground';

  if (state.kind === 'ready') {
    return (
      // No <track>: the transcript is shown right below the player.
      <audio
        src={state.url}
        controls
        autoPlay
        preload="auto"
        className="h-9 w-full max-w-xs"
        aria-label={t('messaging.voicemail.player', 'Voicemail recording')}
        data-testid="voicemail-audio"
      />
    );
  }

  if (state.kind === 'gone') {
    return (
      <p className={cn('text-xs italic', mutedText)} data-testid="voicemail-recording-gone">
        {t('messaging.voicemail.gone', 'Recording no longer available')}
      </p>
    );
  }

  const buttonClass = cn(
    'inline-flex w-fit items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60',
    inverted ? 'border-white/40 text-white hover:bg-white/10' : 'border-border hover:bg-muted',
  );

  if (state.kind === 'error') {
    return (
      <div className="flex flex-wrap items-center gap-2" data-testid="voicemail-play-error">
        <span className={cn('text-xs', inverted ? 'text-white' : 'text-destructive')} role="alert">
          {t('messaging.voicemail.loadFailed', 'Could not load the recording.')}
        </span>
        <button type="button" className={buttonClass} onClick={load} data-testid="voicemail-retry">
          <RotateCcw className="h-3 w-3" aria-hidden="true" />
          {t('messaging.voicemail.retry', 'Retry')}
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      className={buttonClass}
      onClick={load}
      disabled={state.kind === 'loading'}
      data-testid="voicemail-play"
    >
      {state.kind === 'loading' ? (
        <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
      ) : (
        <Play className="h-3 w-3" aria-hidden="true" />
      )}
      {t('messaging.voicemail.play', 'Play voicemail')}
    </button>
  );
}

export default VoicemailPlayer;
